import { SerializedConstructor } from '@langchain/core/load/serializable';

import { union } from 'lodash';
import mustache from 'mustache';

import { extractTemplateFormatFromKwargs } from '../components/manifest/serialized/prompts/ManifestChatPromptTemplate.utils';
import { ETemplateFormat } from './types';

export type VariableNameAndNestedKey = { name: string; nestedKey?: string };

export function getDotSyntaxAndSectionVariablesFromParsedMessage(
  parsed
): VariableNameAndNestedKey[] {
  const dotSyntaxVariables = parsed
    .filter(
      (dotSyntax) => dotSyntax[0] === 'name' && dotSyntax[1].includes('.')
    )
    .map((dotSyntax) => {
      const [name, nestedKey] = dotSyntax[1].split('.');
      return { name, nestedKey } as VariableNameAndNestedKey;
    });

  const sectionVariables = parsed
    .filter((section) => section[0] === '#')
    .map((section) => ({ name: section[1] } as VariableNameAndNestedKey));

  return [...dotSyntaxVariables, ...sectionVariables];
}

// checking for section & dot syntax variables in the entire template so we can render them with a json editor
export function parseSectionAndDotSyntaxVariablesFromMustacheTemplate(
  kwargs: any
): VariableNameAndNestedKey[] {
  const messages = kwargs?.messages;
  if (messages) {
    const variableTypeMap = messages
      .map((message) => {
        try {
          const parsed = mustache.parse(
            message.kwargs?.prompt?.kwargs?.template
          );
          return getDotSyntaxAndSectionVariablesFromParsedMessage(parsed);
        } catch {
          return [];
        }
      })
      .flat();
    return variableTypeMap;
  } else {
    // no messages -> PromptTemplate
    try {
      const parsed = mustache.parse(kwargs?.template);
      return getDotSyntaxAndSectionVariablesFromParsedMessage(parsed);
    } catch {
      return [];
    }
  }
}

function getSectionAndDotSyntaxVariables(
  manifests: SerializedConstructor[]
): VariableNameAndNestedKey[] {
  return union(
    manifests
      .map((manifest) => {
        return extractTemplateFormatFromKwargs(manifest.kwargs.first.kwargs) ===
          ETemplateFormat.MUSTACHE
          ? parseSectionAndDotSyntaxVariablesFromMustacheTemplate(
              manifest.kwargs.first.kwargs
            )
          : [];
      })
      .flat()
  );
}

export function deduplicateVariables(
  variables: VariableNameAndNestedKey[]
): VariableNameAndNestedKey[] {
  return variables.reduce((acc, curr) => {
    const existingVar = acc.find((v) => v.name === curr.name);
    if (!existingVar) {
      acc.push(curr);
    } else if (curr.nestedKey && !existingVar.nestedKey) {
      existingVar.nestedKey = curr.nestedKey;
    }
    return acc;
  }, [] as VariableNameAndNestedKey[]);
}

export function getVariablesRequiringJSONEditor(
  manifests: SerializedConstructor[]
): VariableNameAndNestedKey[] {
  const sectionAndDotSyntaxVariables =
    getSectionAndDotSyntaxVariables(manifests);

  // Deduplicate based on key, prioritizing keeping nested keys
  const dedupedVariables = deduplicateVariables(sectionAndDotSyntaxVariables);
  return dedupedVariables;
}
